import React from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { JobFair } from 'app/types';
import { KTSVG } from '_metronic/helpers';
import { Roles } from 'app/types';
import { useAuth } from 'app/context/auth-context';
import { usTimezones } from 'app/helpers/MiscUtils';

interface CreateEditJobFairFormProps {
    onSubmit: (values: any) => void;
    jobFair?: JobFair;

    orgName: string
}

const CreateEditJobFair: React.FC<CreateEditJobFairFormProps> = ({ onSubmit, jobFair , orgName}) => {
    const isEditMode = !!jobFair;
    const { user } = useAuth();

    const formatDateForInput = (dateString: string) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toISOString().split('T')[0];
    };

    let isAdmin = false;
    if(user?.roles?.includes(Roles.Admin)){
        isAdmin = true;
    }

    let validationStartDateSchema = Yup.date().required('Required');
    if(!isAdmin){
        validationStartDateSchema = validationStartDateSchema.min(new Date(), 'Date must be in the future')
    }

    const formik = useFormik({
        initialValues: {
            name: jobFair?.name || orgName + ' - job fair' || '',
            startDate: formatDateForInput(jobFair?.startDate || new Date(new Date().setDate(new Date().getDate() + 1)).toISOString()),
            startTime: jobFair?.startTime || new Date().toLocaleTimeString('en-US', { hour12: false }).slice(0, 5),
            timezone: jobFair?.timezone || '',
            durationHours: jobFair?.durationHours || 3,
            durationMinutes: jobFair?.durationMinutes || 0,
        },
        validationSchema: Yup.object({
            name: Yup.string().required('Required').max(100, 'Must be 100 characters or less'),
            startDate: validationStartDateSchema,
            startTime: Yup.string().required('Required'),
            timezone: Yup.string().required('Required'),
            durationHours: Yup.number().min(0).required('Required').max(10, 'Must be 10 or less'),
            durationMinutes: Yup.number().min(0).max(59).required('Required'),
        }),
        onSubmit: (values) => {
            onSubmit(values);
        },
    });

    return (
        <form onSubmit={formik.handleSubmit}>
            <div className='fv-row mb-7'>
                <label className='required fw-bold fs-6 mb-2'>Name</label>
                <input
                    type="text"
                    id="name"
                    {...formik.getFieldProps('name')}
                    className={`form-control form-control-solid mb-3 mb-lg-0 ${
                        formik.touched.name && formik.errors.name ? 'is-invalid' : ''
                    }`}
                />
                {formik.touched.name && formik.errors.name && (
                    <div className="text-danger mt-2">{formik.errors.name}</div>
                )}
            </div>

            <div className='fv-row mb-7'>
                <label className='required fw-bold fs-6 mb-2'>Start Date</label>
                <input
                    type="date"
                    id="startDate"
                    min={isAdmin ? '' : new Date().toISOString().split('T')[0]}
                    {...formik.getFieldProps('startDate')}
                    className={`form-control form-control-solid ${
                        formik.touched.startDate && formik.errors.startDate ? 'is-invalid' : ''
                    }`}
                />
                {formik.touched.startDate && formik.errors.startDate && (
                    <div className="text-danger mt-2">{formik.errors.startDate}</div>
                )}
            </div>

            <div className='fv-row mb-7'>
                <label className='required fw-bold fs-6 mb-2'>Start Time</label>
                <div className='position-relative'>
                    <input
                        type="time"
                        id="startTime"
                        {...formik.getFieldProps('startTime')}
                        className={`form-control form-control-solid ${
                            formik.touched.startTime && formik.errors.startTime ? 'is-invalid' : ''
                        }`}
                    />
                    <div className='position-absolute translate-middle-y top-50 end-0 me-4'>
                        <KTSVG path='/media/icons/duotone/Home/Alarm-clock.svg' className='svg-icon-2' />
                    </div>
                </div>
                {formik.touched.startTime && formik.errors.startTime && (
                    <div className="text-danger mt-2">{formik.errors.startTime}</div>
                )}
            </div>

            <div className='fv-row mb-7'>
                <label className='required fw-bold fs-6 mb-2'>Timezone</label>
                <select
                    id="timezone"
                    {...formik.getFieldProps('timezone')}
                    className='form-select form-select-solid'
                >
                    <option value="">Select Timezone</option>
                    {usTimezones.map((timezone) => (
                        <option key={timezone} value={timezone}>
                            {timezone}
                        </option>
                    ))}
                </select>
                {formik.touched.timezone && formik.errors.timezone && (
                    <div className="text-danger mt-2">{formik.errors.timezone}</div>
                )}
            </div>

            <div className='fv-row mb-7'>
                <label className='required fw-bold fs-6 mb-2'>Duration</label>
                <div className='d-flex'>
                    <div className='me-5'>
                        <input
                            type="number"
                            id="durationHours"
                            {...formik.getFieldProps('durationHours')}
                            className='form-control form-control-solid w-100px'
                            min="0"
                            max="10"
                        />
                        <span className='text-muted ms-2'>Hours</span>
                    </div>
                    <div>
                        <input
                            type="number"
                            id="durationMinutes"
                            {...formik.getFieldProps('durationMinutes')}
                            className='form-control form-control-solid w-100px'
                            min="0"
                            max="59"
                        />
                        <span className='text-muted ms-2'>Minutes</span>
                    </div>
                </div>
            </div>

            <div className='text-center pt-15'>
                <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={formik.isSubmitting}
                >
                    {formik.isSubmitting ? 'Processing...' : (isEditMode ? 'Update' : 'Create & Pay')}
                </button>
            </div>
        </form>
    );
};

export default CreateEditJobFair;
